import { Loader } from 'lucide-react';
import DatePickerDD from '@/components/DatePickerDD';
import MerchantBankInfo from './MerchantBankInfo';

const PHONE_CODES = ['0414', '0424', '0412', '0416', '0426', '0422', '0212'];
const DOC_TYPES = ['V', 'E', 'J', 'G'];

const INSTRUCTIONS = {
  p2p: 'Realiza el pago móvil e ingresa los datos de la transacción',
  c2p: 'Solicita el token a tu banco e ingrésalo junto con tus datos',
  bcv_transfer: 'Realiza la transferencia bancaria a la cuenta indicada e ingresa la referencia',
};

export default function PaymentForm({
  order,
  banks,
  selectedMethod,
  paymentForm,
  setPaymentForm,
  processing,
  onSubmit,
}) {
  const isP2POrC2P = selectedMethod === 'p2p' || selectedMethod === 'c2p';
  const isBcv = selectedMethod === 'bcv_transfer';
  const isC2P = selectedMethod === 'c2p';

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="payment-form">
      <div className="border-2 border-blue-100 rounded-xl p-4 mb-4" style={{ backgroundColor: '#E8F3FA' }}>
        <p className="text-sm font-medium" style={{ color: '#003594' }}>
          {INSTRUCTIONS[selectedMethod]}
        </p>
      </div>

      {isBcv && <MerchantBankInfo order={order} banks={banks} />}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {isBcv ? 'Tu Banco (origen) *' : 'Banco *'}
          </label>
          <select
            data-testid="payment-bank-select"
            value={paymentForm.bankCode}
            onChange={(e) => setPaymentForm({ ...paymentForm, bankCode: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500 transition-all"
            style={{ fontSize: '15px' }}
            required
          >
            <option value="">Seleccionar banco...</option>
            {banks.map((bank) => (
              <option key={bank.codigo} value={bank.codigo}>
                ({bank.codigo}) {bank.nombre}
              </option>
            ))}
          </select>
        </div>

        {isP2POrC2P && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono *</label>
            <div className="grid grid-cols-3 gap-2">
              <select
                value={paymentForm.phoneCode}
                onChange={(e) => setPaymentForm({ ...paymentForm, phoneCode: e.target.value })}
                className="px-3 py-3 border-2 border-gray-300 rounded-xl text-center font-semibold"
                style={{ fontSize: '15px' }}
                required
              >
                {PHONE_CODES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="text"
                value={paymentForm.phone}
                onChange={(e) => setPaymentForm({ ...paymentForm, phone: e.target.value })}
                className="col-span-2 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
                style={{ fontSize: '15px' }}
                placeholder="1234567"
                maxLength={7}
                required
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Referencia *</label>
          <input
            type="text"
            data-testid="payment-reference-input"
            value={paymentForm.reference}
            onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
            style={{ fontSize: '15px' }}
            placeholder={isBcv ? 'Número de referencia' : 'Últimos 6 dígitos (ej: 575987)'}
            maxLength={20}
            required
          />
          <p className="text-xs text-gray-600 mt-1 font-medium">
            {isBcv
              ? 'Número de referencia de la transferencia'
              : 'Los últimos 6 dígitos de tu pago móvil'}
          </p>
        </div>

        {isC2P && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Token *</label>
            <input
              type="text"
              value={paymentForm.token}
              onChange={(e) => setPaymentForm({ ...paymentForm, token: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
              style={{ fontSize: '15px' }}
              placeholder="Token del banco"
              minLength={6}
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha *</label>
          <DatePickerDD
            value={paymentForm.date}
            onChange={(val) => setPaymentForm({ ...paymentForm, date: val })}
            placeholder="27/08/2025"
          />
        </div>

        {isP2POrC2P && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Documento *</label>
            <div className="grid grid-cols-4 gap-2">
              <select
                value={paymentForm.docType}
                onChange={(e) => setPaymentForm({ ...paymentForm, docType: e.target.value })}
                className="px-3 py-3 border-2 border-gray-300 rounded-xl text-center font-semibold"
                style={{ fontSize: '15px' }}
                required
              >
                {DOC_TYPES.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <input
                type="text"
                value={paymentForm.docNumber}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, docNumber: e.target.value.replace(/[^0-9]/g, '') })
                }
                className="col-span-3 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
                style={{ fontSize: '15px' }}
                placeholder="12345678"
                maxLength={10}
                required
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Monto *</label>
          <input
            type="number"
            step="0.01"
            data-testid="payment-amount-input"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500 font-semibold"
            style={{ fontSize: '15px' }}
            required
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="verify-payment-btn"
        disabled={processing}
        className="w-full py-4 px-4 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
        style={{ backgroundColor: '#003594' }}
      >
        {processing ? (
          <>
            <Loader className="w-6 h-6 animate-spin" />
            Verificando Pago...
          </>
        ) : (
          'Verificar Pago'
        )}
      </button>
    </form>
  );
}
